import { SERVER_URL } from '@aca-new/app/shared/constants/app.constants';
import { ICity } from '@aca-new/app/shared/models/interfaces/city.interface';
import { ICountry } from '@aca-new/app/shared/models/interfaces/country.interface';
import { IGeoLocationSearchItem } from '@aca-new/app/shared/models/interfaces/geo-location-search-item.interface';
import { IGeoLocationSearchResult } from '@aca-new/app/shared/models/interfaces/geo-location-search-result.interface';
import { IGeoPlanet } from '@aca-new/app/shared/models/interfaces/geo-planet.interface';
import { HttpResponseBodyNullableType, IHttpResponseBody } from '@aca-new/app/shared/models/interfaces/http-response-body.interface';
import { ILabelValue } from '@aca-new/app/shared/models/interfaces/label-value.interface';
import { IAddressExtractResponse, IMapAddressResult } from '@aca-new/app/shared/models/interfaces/map-address-result.interface';
import { IOffice } from '@aca-new/app/shared/models/interfaces/office.interface';
import { ISelect } from '@aca-new/app/shared/models/interfaces/select.interface';
import { ISubLocation } from '@aca-new/app/shared/models/interfaces/sub-location.interface';
import { QimaNullableType } from '@aca-new/app/shared/models/types/qima.type';
import { HttpService } from '@aca-new/app/shared/services/http-services/http.service';
import { HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

const ADDRESS_EXTRACT_TYPE: string = 'AI_ADDRESS_WITH_PHONE';

@Injectable()
export class AppLocationHttpService {
  public constructor(
    private readonly _httpService: HttpService,
    private readonly _translateService: TranslateService
  ) {}

  public getAllCountries$(shouldRefresh: boolean = false): Observable<ICountry[]> {
    const url = `${SERVER_URL}/parameter/v2/countries?refresh=${shouldRefresh}`;

    return this._httpService.httpClient
      .get<IHttpResponseBody<QimaNullableType<ICountry[]>>>(url, {
        observe: 'response',
        headers: {
          lang: this._translateService.currentLang || this._translateService.getDefaultLang(),
        },
      })
      .pipe(map((response: Readonly<HttpResponse<IHttpResponseBody<QimaNullableType<ICountry[]>>>>): ICountry[] => response?.body?.content || []));
  }

  public getCountriesByName$(search: string): Observable<HttpResponseBodyNullableType<ICountry[]>> {
    return this._httpService.httpClient
      .get<HttpResponseBodyNullableType<ICountry[]>>(`${SERVER_URL}/parameter/v2/searchCountries?name=${search}&rows=50`, {
        observe: 'body',
        headers: {
          lang: this._translateService.currentLang || this._translateService.getDefaultLang(),
        },
      })
      .pipe(map((response: Readonly<HttpResponseBodyNullableType<ICountry[]>>): HttpResponseBodyNullableType<ICountry[]> => response));
  }

  public getAllProvincesOfCountry$(countryId: Readonly<string>, shouldRefresh: boolean = false): Observable<ILabelValue[]> {
    return this._httpService.httpClient
      .get<HttpResponseBodyNullableType<ICity[]>>(`${SERVER_URL}/parameter/v2/country/${countryId}/provinces?refresh=${shouldRefresh}`, {
        observe: 'body',
        headers: {
          lang: this._translateService.currentLang || this._translateService.getDefaultLang(),
        },
      })
      .pipe(map((response): ICity[] => response?.content || []));
  }

  public getAllCitiesOfCountry$(countryId: Readonly<string>, shouldRefresh: boolean = false): Observable<HttpResponseBodyNullableType<ICity[]>> {
    return this._httpService.httpClient.get(`${SERVER_URL}/parameter/v2/country/${countryId}/cities?refresh=${shouldRefresh}`, {
      observe: 'body',
      headers: {
        lang: this._translateService.currentLang || this._translateService.getDefaultLang(),
      },
    });
  }

  public getAllCitiesOfProvince$(provinceId: Readonly<string>, shouldRefresh: boolean = true): Observable<ILabelValue[]> {
    return this._httpService.httpClient
      .get<HttpResponseBodyNullableType<ICity[]>>(`${SERVER_URL}/parameter/v2/province/${provinceId}/cities?refresh=${shouldRefresh}`, {
        observe: 'body',
        headers: {
          lang: this._translateService.currentLang || this._translateService.getDefaultLang(),
        },
      })
      .pipe(map((response): ICity[] => response?.content || []));
  }

  public getCitiesByName$(country: string, search: string): Observable<HttpResponseBodyNullableType<ICity[]>> {
    return this._httpService.httpClient
      .get<HttpResponseBodyNullableType<ICity[]>>(`${SERVER_URL}/parameter/v2/country/${country}/searchCities?name=${search}&rows=50`, {
        observe: 'body',
        headers: {
          lang: this._translateService.currentLang || this._translateService.getDefaultLang(),
        },
      })
      .pipe(map((response: Readonly<HttpResponseBodyNullableType<ICity[]>>): HttpResponseBodyNullableType<ICity[]> => response));
  }

  public getCityInfoById$(id: string, isContainProvinceName: boolean = true): Observable<QimaNullableType<ISelect>> {
    let url = `${SERVER_URL}/parameter/v2/searchCitiesPrefill/${id}`;

    if (isContainProvinceName) {
      url += `?containProvinceName=${isContainProvinceName}`;
    }

    return this._httpService.httpClient
      .get<IHttpResponseBody<QimaNullableType<ISelect>>>(url, {
        observe: 'response',
        headers: {
          lang: this._translateService.currentLang || this._translateService.getDefaultLang(),
        },
      })
      .pipe(map((response: Readonly<HttpResponse<IHttpResponseBody<QimaNullableType<ISelect>>>>): QimaNullableType<ISelect> => response?.body?.content || null));
  }

  public getCountryOrRegions$(): Observable<QimaNullableType<ISelect[]>> {
    return this._httpService.httpClient
      .get<IHttpResponseBody<QimaNullableType<ISelect[]>>>(`${SERVER_URL}/parameter/v2/countries-with-calling-country-code?refresh=false`, {
        observe: 'response',
        headers: {
          lang: this._translateService.currentLang || this._translateService.getDefaultLang(),
        },
      })
      .pipe(map((response: Readonly<HttpResponse<IHttpResponseBody<QimaNullableType<ISelect[]>>>>): QimaNullableType<ISelect[]> => response?.body?.content || null));
  }

  public getOffices$(): Observable<IOffice[]> {
    return this._httpService.httpClient
      .get<IOffice[]>(`${SERVER_URL}/parameter/ai-offices`, {
        observe: 'body',
      })
      .pipe(map((response: IOffice[]): IOffice[] => response));
  }

  public getDistrictsOfCity$(cityId: Readonly<string>, keywords: string = ''): Observable<ISubLocation[]> {
    return this._httpService.httpClient
      .get<HttpResponseBodyNullableType<ISubLocation[]>>(`${SERVER_URL}/parameter/v2/sub-locations?parentId=${cityId}&placeType=District&keywords=${keywords}`, {
        observe: 'body',
        headers: {
          lang: this._translateService.currentLang || this._translateService.getDefaultLang(),
        },
      })
      .pipe(map((response): ISubLocation[] => response?.content || []));
  }

  public getGeoPlanet$(id: string): Observable<QimaNullableType<IGeoPlanet>> {
    return this._httpService.httpClient
      .get<IHttpResponseBody<QimaNullableType<IGeoPlanet>>>(`${SERVER_URL}/parameter/v2/geo-planet/${id}`, {
        observe: 'body',
        headers: {
          lang: this._translateService.currentLang || this._translateService.getDefaultLang(),
        },
      })
      .pipe(map((response): QimaNullableType<IGeoPlanet> => response?.content ?? null));
  }

  /**
   * Keyword search across locations (country / province / city / district).
   * Public endpoint: AuthInterceptor skips user login and only sends ai-api-access-token.
   * @param {string} keyword - Search keyword (min length 2)
   * @returns {Observable<IGeoLocationSearchResult[]>} Search results with hierarchy ids and path label
   */
  public searchLocationsByKeyword$(keyword: string): Observable<IGeoLocationSearchResult[]> {
    const normalizedKeyword: string = keyword.trim();

    if (normalizedKeyword.length < 2) {
      return of([]);
    }

    const url: string = `${SERVER_URL}/parameter/v2/geo/location/search?name=${encodeURIComponent(normalizedKeyword)}`;

    return this._httpService.httpClient
      .get<HttpResponseBodyNullableType<IGeoLocationSearchItem[]>>(url, {
        observe: 'body',
        headers: {
          lang: this._translateService.currentLang || this._translateService.getDefaultLang(),
        },
      })
      .pipe(
        map((response: HttpResponseBodyNullableType<IGeoLocationSearchItem[]>): IGeoLocationSearchResult[] => {
          const items: IGeoLocationSearchItem[] = response?.content || [];

          return items
            .map((item: IGeoLocationSearchItem): IGeoLocationSearchResult => {
              const placetype: string = item.placetype || '';
              const label: string = [item.countryName, item.provinceName, item.cityName, item.districtName]
                .filter((part: string | undefined): part is string => !!part)
                .join(' / ');
              const countryId: string = item.countryId || (placetype === 'Country' ? item.id : '') || '';
              const provinceId: string = item.provinceId || (placetype === 'Province' ? item.id : '') || '';
              const cityId: string = item.cityId || (placetype === 'City' ? item.id : '') || '';
              const districtId: string = placetype === 'District' ? item.id : '';

              return {
                label,
                id: item.id || '',
                placetype,
                countryId,
                provinceId,
                cityId,
                districtId,
              };
            })
            .filter((item: IGeoLocationSearchResult): boolean => this._isCompleteGeoSearchResult(item));
        })
      );
  }

  /**
   * AI address recognition via public address-extract API.
   * @param {string} address - Free-text address to extract
   * @returns {Observable<IMapAddressResult>} Parsed geo hierarchy and street fields
   */
  public mapAddress$(address: string): Observable<IMapAddressResult> {
    const params: HttpParams = new HttpParams().set('address', address).set('type', ADDRESS_EXTRACT_TYPE);

    return this._httpService.httpClient
      .get<IAddressExtractResponse>(`${SERVER_URL}/v1.0/address-extract`, {
        observe: 'body',
        params,
      })
      .pipe(
        map((response: Readonly<IAddressExtractResponse>): IMapAddressResult => {
          const payload: QimaNullableType<IMapAddressResult> | undefined = response.content ?? response.data;

          if ((response.code != null && response.code !== 0) || !payload) {
            throw new Error(response.message || 'ADDRESS_EXTRACT_FAILED');
          }

          return payload;
        })
      );
  }

  /**
   * Drops incomplete hierarchy rows (e.g. District without cityId) so they are not selectable.
   * @param {IGeoLocationSearchResult} item - Mapped search result
   * @returns {boolean} True when the result has a complete id chain for its placetype
   */
  private _isCompleteGeoSearchResult(item: IGeoLocationSearchResult): boolean {
    if (!item.label || !item.id) {
      return false;
    }

    switch (item.placetype) {
      case 'District':
        return !!item.countryId && !!item.provinceId && !!item.cityId && !!item.districtId;
      case 'City':
        return !!item.countryId && !!item.provinceId && !!item.cityId;
      case 'Province':
        return !!item.countryId && !!item.provinceId;
      case 'Country':
        return !!item.countryId;
      default:
        return false;
    }
  }
}
